import {debounce} from "_lib/utils/utils";

type Options = {
    backgroundColor?: string;
    color?: string;
    size?: number;
    position?: string;
    radius?: number;
    src?: string;
    onChange?: () => void;
};

enum InitStatus {
    NotStarted = 0,
    OnGoing,
    Done,
}

class Badger {
    private canvas: HTMLCanvasElement;
    private ctx: CanvasRenderingContext2D;
    private faviconEL: HTMLLinkElement;
    private img: HTMLImageElement;
    private faviconSize: number;
    private badgeSize: number;
    private offset: { x: number, y: number };
    private value: number = 0;
    private initStatus: InitStatus = InitStatus.NotStarted;

    // 公共属性，用于存储未读消息和红点显示状态
    public unreadMessageByRoomId: Record<string, {count: number, showRedPoint: boolean}> = {};
    public unreadPMCount: number = 0;
    public showRedPoint: boolean = false;

    // 可配置的公共属性
    backgroundColor: string;
    color: string;
    size: number;
    position: string;
    radius: number;
    src: string;
    onChange: () => void;

    constructor(options: Options) {
        Object.assign(
            this, {
                backgroundColor: "#f00",
                color: "#fff",
                size: 0.6,
                position: "se",
                radius: 8,
                src: "",
                onChange: () => { },
            },
            options
        );

        this.canvas = document.createElement("canvas");
        this.faviconEL = document.querySelector("link[rel$=icon]") as HTMLLinkElement;
        this.src = this.src || this.faviconEL && this.faviconEL.getAttribute("href");
        this.ctx = this.canvas.getContext("2d") as CanvasRenderingContext2D;

        this.update = debounce(this.update, 1000)
    }

    private _drawIcon(): void {
        this.ctx.clearRect(0, 0, this.faviconSize, this.faviconSize);
        this.ctx.drawImage(this.img, 0, 0, this.faviconSize, this.faviconSize);
    }

    private _drawShape() {
        let badgeSize = this.badgeSize
        let badgeOffset = 0
        if (this.showRedPoint && !this.value) {
            badgeOffset = badgeSize * 0.15
            badgeSize *= 0.8
        }

        const r = this.radius;
        const xa = this.offset.x + badgeOffset;
        const ya = this.offset.y + badgeOffset;
        const xb = this.offset.x + badgeSize + badgeOffset;
        const yb = this.offset.y + badgeSize + badgeOffset;
        this.ctx.beginPath();
        this.ctx.moveTo(xb - r, ya);
        this.ctx.quadraticCurveTo(xb, ya, xb, ya + r);
        this.ctx.lineTo(xb, yb - r);
        this.ctx.quadraticCurveTo(xb, yb, xb - r, yb);
        this.ctx.lineTo(xa + r, yb);
        this.ctx.quadraticCurveTo(xa, yb, xa, yb - r);
        this.ctx.lineTo(xa, ya + r);
        this.ctx.quadraticCurveTo(xa, ya, xa + r, ya);
        this.ctx.fillStyle = this.backgroundColor;
        this.ctx.fill();
        this.ctx.closePath();
    }

    private _drawVal() {
        const margin = (this.badgeSize * 0.18) / 2;
        this.ctx.beginPath();
        this.ctx.textBaseline = "middle";
        this.ctx.textAlign = "center";
        this.ctx.font = `bold ${this.badgeSize * 0.82}px Arial`;
        this.ctx.fillStyle = this.color;
        this.ctx.fillText(String(this.value), this.badgeSize / 2 + this.offset.x, this.badgeSize / 2 + this.offset.y + margin);
        this.ctx.closePath();
    }

    private _drawFavicon() {
        this.faviconEL.setAttribute("href", this.dataURL);
    }

    private _draw() {
        this._drawIcon();
        if (this.showRedPoint) this._drawShape();
        if (this.value) this._drawVal();
        this._drawFavicon();
    }

    private _setup() {
        this.faviconSize = this.img.naturalWidth;
        this.badgeSize = this.faviconSize * this.size;
        this.canvas.width = this.faviconSize;
        this.canvas.height = this.faviconSize;
        const sd = this.faviconSize - this.badgeSize;
        const sd2 = sd / 2;
        this.offset = {
            n:  {x: sd2, y: 0 },
            e:  {x: sd, y: sd2},
            s:  {x: sd2, y: sd},
            w:  {x: 0, y: sd2},
            nw: {x: 0, y: 0},
            ne: {x: sd, y: 0},
            sw: {x: 0, y: sd},
            se: {x: sd, y: sd},
        }[this.position];
    }
    update(): void {
        let showRedPoint = false
        this.value = Object.values(this.unreadMessageByRoomId).reduce(
            (acc, cur) => {
                showRedPoint ||= cur.showRedPoint
                return acc + cur.count
            }
            , 0) + this.unreadPMCount;
        this.showRedPoint = showRedPoint || this.unreadPMCount > 0

        this.value = Math.min(99, parseInt(String(this.value), 10));
        if (this.initStatus === InitStatus.Done) {
            this._draw();
            if (this.onChange) this.onChange.call(this);
        } else if (this.initStatus === InitStatus.NotStarted) {
            this.initStatus = InitStatus.OnGoing
            this.img = new Image();
            this.img.crossOrigin = 'anonymous'
            this.img.addEventListener("load", () => {
                this._setup();
                this.initStatus = InitStatus.Done;
                this._draw();
                if (this.onChange) this.onChange.call(this);
            });
            this.img.src = this.src + `?t=${Date.now()}`
        }
    }

    updateRoomUnreadCount(count: number, roomId: string) {
        let showRedPoint = count > 0 
        this.unreadMessageByRoomId[roomId] = {count, showRedPoint}
        this.update()
    }

    updatePMUnreadCount(count: number) {
        console.log("updatePmUnreadCount")
        this.unreadPMCount = count
        this.update()
    }

    get dataURL(): string {
        return this.canvas.toDataURL();
    }
}

export default new Badger({})